<script setup lang="ts">
import { ref } from "vue";
import { useApiClient } from "@/api/client";
import { useI18n } from "vue-i18n";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";

const { t } = useI18n();

const breadcrumbs = [
	{
		title: t("targets.overview.title"),
		disabled: false,
		to: "/targets",
	},
	{
		title: t("targets.import.title"),
		disabled: true,
		to: "/targets/import",
	},
];

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const uploading = ref(false);
const errorMessage = ref("");
const success = ref(false);

const triggerFileInput = () => {
	if (!uploading.value) {
		fileInputRef.value?.click();
	}
};

const onFileSelected = (event: Event) => {
	const input = event.target as HTMLInputElement;
	if (input.files?.[0]) {
		setFile(input.files[0]);
	}
};

const onDrop = (event: DragEvent) => {
	isDragging.value = false;
	if (!uploading.value && event.dataTransfer?.files?.[0]) {
		setFile(event.dataTransfer.files[0]);
	}
};

const setFile = (file: File) => {
	selectedFile.value = file;
	errorMessage.value = "";
	success.value = false;
};

const uploadFile = async () => {
	if (!selectedFile.value) return;

	uploading.value = true;
	errorMessage.value = "";
	success.value = false;

	const response = await useApiClient("bookingExperts").importTargets(
		selectedFile.value
	);

	uploading.value = false;

	if (response.isOk()) {
		success.value = true;
		selectedFile.value = null;
		if (fileInputRef.value) {
			fileInputRef.value.value = "";
		}
	} else if (response.isErr()) {
		errorMessage.value = response.error.message;
	}
};
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<v-card class="mt-5 pa-6">
		<div
			class="upload-zone rounded-lg d-flex flex-column align-center justify-center"
			:class="{ 'is-dragging': isDragging, 'is-disabled': uploading }"
			@click="triggerFileInput"
			@dragover.prevent="isDragging = true"
			@dragleave.prevent="isDragging = false"
			@drop.prevent="onDrop"
		>
			<v-icon
				size="56"
				:color="selectedFile ? 'primary' : 'medium-emphasis'"
				class="mb-3"
			>
				{{ selectedFile ? "mdi-file-check-outline" : "mdi-cloud-upload-outline" }}
			</v-icon>

			<template v-if="selectedFile">
				<p class="text-subtitle-1 font-weight-medium">{{ selectedFile.name }}</p>
				<p class="text-body-2 text-medium-emphasis mt-1">
					{{ (selectedFile.size / 1024).toFixed(1) }} KB —
					<span class="text-primary">{{ t("targets.import.dropZone.changeFile") }}</span>
				</p>
			</template>
			<template v-else>
				<p class="text-subtitle-1 font-weight-medium">
					{{ t("targets.import.dropZone.title") }}
				</p>
				<p class="text-body-2 text-medium-emphasis mt-1">
					{{ t("targets.import.dropZone.subtitle") }}
				</p>
			</template>

			<input
				ref="fileInputRef"
				type="file"
				accept=".xlsx"
				class="d-none"
				@change="onFileSelected"
			/>
		</div>

		<v-progress-linear
			v-if="uploading"
			indeterminate
			color="primary"
			class="mt-4"
			rounded
		/>

		<v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
			{{ errorMessage }}
		</v-alert>

		<v-alert v-if="success" type="success" variant="tonal" class="mt-4">
			{{ t("targets.import.success") }}
		</v-alert>

		<div class="d-flex ga-3 mt-5">
			<v-btn
				color="primary"
				variant="flat"
				:disabled="!selectedFile || uploading"
				:loading="uploading"
				@click="uploadFile"
			>
				{{ t("targets.import.submit") }}
			</v-btn>

			<v-btn variant="text" to="/targets" :disabled="uploading">
				{{ t("targets.import.cancel") }}
			</v-btn>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.upload-zone {
	border: 2px dashed rgba(var(--v-border-color), 0.5);
	min-height: 220px;
	cursor: pointer;
	transition:
		border-color 0.2s,
		background-color 0.2s;
	padding: 48px 32px;

	&:hover:not(.is-disabled) {
		border-color: rgba(var(--v-theme-primary), 0.6);
		background-color: rgba(var(--v-theme-primary), 0.03);
	}

	&.is-dragging {
		border-color: rgb(var(--v-theme-primary));
		background-color: rgba(var(--v-theme-primary), 0.06);
	}

	&.is-disabled {
		opacity: 0.6;
		cursor: default;
	}
}
</style>
